import Rails from '@rails/ujs'
import EmblaCarousel from 'embla-carousel'
import * as CookieConsent from 'vanilla-cookieconsent'
import {
  ArrowRight,
  ArrowLeft,
  ArrowDown,
  ArrowUpRight,
  BookOpen,
  ChevronRight,
  ChevronDown,
  CookingPot,
  Grid2X2,
  List,
  Menu,
  Search,
  Star,
  Tag,
  X,
  createIcons,
} from 'lucide'

import '../styles/application.css'
import '../javascript/controllers/public'
import 'vanilla-cookieconsent/dist/cookieconsent.css'

if (!document.documentElement.dataset.adminArea) {
  CookieConsent.run({
    revision: 2,
    cookie: { name: 'mbene_cookie_consent', expiresAfterDays: 182, sameSite: 'Lax' },
    guiOptions: { consentModal: { layout: 'box wide', position: 'bottom center', equalWeightButtons: false }, preferencesModal: { layout: 'box' } },
    categories: { necessary: { enabled: true, readOnly: true }, analytics: {}, advertising: {} },
    language: { default: 'it', translations: { it: { consentModal: { title: 'La tua privacy', description: 'Usiamo cookie tecnici necessari e, solo con il tuo consenso, cookie statistici e pubblicitari.', acceptAllBtn: 'Accetta tutto', acceptNecessaryBtn: 'Solo necessari', showPreferencesBtn: 'Gestisci preferenze', footer: '<a href="/cookie">Leggi la Cookie Policy</a>' }, preferencesModal: { title: 'Preferenze cookie', acceptAllBtn: 'Accetta tutto', acceptNecessaryBtn: 'Solo necessari', savePreferencesBtn: 'Salva preferenze', sections: [{ title: 'Cookie necessari', description: 'Servono al funzionamento sicuro del sito.', linkedCategory: 'necessary' }, { title: 'Statistiche', description: 'Google Analytics ci aiuta a capire come viene usato il sito.', linkedCategory: 'analytics' }, { title: 'Pubblicità', description: 'Google AdSense permette di mostrare annunci.', linkedCategory: 'advertising' }] } } } }
  })
}

if (!window._rails_loaded) Rails.start()

document.addEventListener('DOMContentLoaded', () => {
  const lucideIcons = { ArrowDown, ArrowLeft, ArrowRight, ArrowUpRight, BookOpen, ChevronDown, ChevronRight, CookingPot, Grid2x2: Grid2X2, List, Menu, Search, Star, Tag, X }

  createIcons({
    // Lucide normalizes `grid-2x2` to `Grid2x2`; retain the documented export as an alias.
    icons: lucideIcons,
  })

  const header = document.querySelector('[data-site-header]')
  const mobileMenu = document.querySelector('[data-mobile-menu]')
  const searchPanel = document.querySelector('[data-search-panel]')
  const filterDrawer = document.querySelector('[data-filter-drawer]')
  const filterDrawerToggle = document.querySelector('[data-filter-drawer-toggle]')
  const megaMenus = [...document.querySelectorAll('[data-mega-menu]')]

  const transitionPanel = (panel, open, afterClose = null) => {
    if (!panel) return
    panel.hidden = !open
    if (!open) afterClose?.()
  }

  const initializeCarousel = (root) => {
    const viewport = root?.querySelector('[data-embla-viewport]')
    if (!viewport) return

    if (root.emblaApi) {
      root.emblaApi.reInit()
      return
    }

    const pager = root.querySelector('[data-embla-pager]')
    const autoPager = root.hasAttribute('data-embla-auto-pager')
    const status = root.querySelector('[data-embla-status]')
    let dots = [...root.querySelectorAll('[data-embla-dot]')]
    const previousButton = root.querySelector('[data-embla-prev]')
    const nextButton = root.querySelector('[data-embla-next]')
    const emblaOptions = root.hasAttribute('data-embla-recent')
      ? { align: 'start', loop: false, slidesToScroll: 1, breakpoints: { '(min-width: 1024px)': { slidesToScroll: 5 } } }
      : root.hasAttribute('data-embla-categories')
        ? { align: 'start', loop: false, slidesToScroll: 1 }
        : { loop: true }
    const emblaApi = EmblaCarousel(viewport, emblaOptions)

    const buildPager = () => {
      if (!autoPager || !pager) return

      pager.replaceChildren()
      dots = emblaApi.scrollSnapList().map((_, index) => {
        const dot = document.createElement('button')
        dot.type = 'button'
        dot.className = 'size-2.5 rounded-full bg-ink/20 transition hover:bg-brand-red data-[selected]:w-7 data-[selected]:bg-brand-red'
        dot.setAttribute('aria-label', `Mostra pagina ${index + 1}`)
        dot.dataset.emblaDot = index
        dot.addEventListener('click', () => emblaApi.scrollTo(index))
        return dot
      })
      pager.append(...dots)
    }

    const updatePager = () => {
      const selectedIndex = emblaApi.selectedScrollSnap()
      dots.forEach((dot, index) => {
        dot.toggleAttribute('data-selected', index === selectedIndex)
        dot.setAttribute('aria-current', index === selectedIndex ? 'true' : 'false')
      })
      previousButton?.toggleAttribute('disabled', !emblaApi.canScrollPrev())
      nextButton?.toggleAttribute('disabled', !emblaApi.canScrollNext())
      if (status) status.textContent = `Pagina ${selectedIndex + 1} di ${emblaApi.scrollSnapList().length}`
    }

    buildPager()
    if (!autoPager) dots.forEach((dot, index) => dot.addEventListener('click', () => emblaApi.scrollTo(index)))
    previousButton?.addEventListener('click', () => emblaApi.scrollPrev())
    nextButton?.addEventListener('click', () => emblaApi.scrollNext())
    emblaApi.on('select', updatePager)
    emblaApi.on('reInit', () => {
      buildPager()
      updatePager()
    })
    updatePager()
    root.emblaApi = emblaApi
  }

  document.querySelectorAll('[data-embla]').forEach(initializeCarousel)

  // Content remains visible by default. The observer only adds an animation
  // after an element enters the viewport, so it can never blank the page.
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const revealTargets = [...new Set([
    ...document.querySelectorAll('main > section, main > article, main > div > section, main > div > article, [data-reveal]'),
    ...document.querySelectorAll('[data-recipe-list] > *'),
    ...document.querySelectorAll('[aria-labelledby="home-recipe-categories-title"] article'),
    ...document.querySelectorAll('.blog-article-body > :is(p, h2, h3, h4, blockquote, ul, ol, img, figure)'),
  ])].filter((target) => !target.querySelector('[data-recipe-list]') && !target.matches('[aria-labelledby="home-recipe-categories-title"]'))
  if (!reducedMotion && 'IntersectionObserver' in window) {
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        entry.target.removeAttribute('data-reveal-pending')
        entry.target.setAttribute('data-revealed', '')
        revealObserver.unobserve(entry.target)
      })
    // A percentage threshold is calculated against the target's full height.
    // On a long article, a phone viewport can never cover 8% of the element,
    // leaving its entire contents hidden. Reveal as soon as it enters instead.
    }, { rootMargin: '0px 0px -8%', threshold: 0 })
    revealTargets.forEach((target) => {
      target.setAttribute('data-reveal-pending', '')
      revealObserver.observe(target)
    })
  }

  const setBodyLock = () => {
    // Do not lock the document root for search: it is the sticky header's
    // containing scrollport in several browsers. The search panel already
    // occupies the remaining viewport and scrolls independently.
    const overlayOpen = (mobileMenu && !mobileMenu.hidden) || (filterDrawer && !filterDrawer.hidden)
    document.documentElement.classList.remove('overflow-hidden')
    document.body.classList.toggle('overflow-hidden', overlayOpen)
  }

  const closeFilterDrawer = () => {
    if (!filterDrawer || filterDrawer.hidden) return
    filterDrawer.hidden = true
    filterDrawerToggle?.setAttribute('aria-expanded', 'false')
    setBodyLock()
    filterDrawerToggle?.focus()
  }

  filterDrawerToggle?.addEventListener('click', () => {
    if (!filterDrawer) return
    filterDrawer.hidden = false
    filterDrawerToggle.setAttribute('aria-expanded', 'true')
    setBodyLock()
    filterDrawer.querySelector('[data-filter-drawer-initial-focus]')?.focus()
  })

  filterDrawer?.querySelectorAll('[data-filter-drawer-close]').forEach((button) => {
    button.addEventListener('click', closeFilterDrawer)
  })

  const closeMegaMenus = (except = null) => {
    megaMenus.forEach((menu) => {
      if (menu === except) return
      transitionPanel(menu, false)
      document.querySelector(`[aria-controls="${menu.id}"]`)?.setAttribute('aria-expanded', 'false')
    })
  }

  document.querySelectorAll('[data-mega-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const menu = document.getElementById(button.getAttribute('aria-controls'))
      const willOpen = menu?.hidden || menu?.dataset.panelState === 'closing'
      closeMegaMenus(menu)
      if (menu) transitionPanel(menu, willOpen)
      button.setAttribute('aria-expanded', String(Boolean(willOpen)))
      if (willOpen) menu?.querySelectorAll('[data-embla]').forEach(initializeCarousel)
    })
  })

  document.querySelectorAll('[data-mobile-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      if (!mobileMenu) return
      mobileMenu.hidden = !mobileMenu.hidden
      if (mobileMenu.hidden) closeMobilePanels()
      button.setAttribute('aria-expanded', String(!mobileMenu.hidden))
      button.setAttribute('aria-label', mobileMenu.hidden ? 'Apri il menu' : 'Chiudi il menu')
      button.innerHTML = `<i data-lucide="${mobileMenu.hidden ? 'menu' : 'x'}" class="size-5" aria-hidden="true"></i>`
      createIcons({ icons: lucideIcons })
      setBodyLock()
    })
  })

  const mobilePanels = mobileMenu ? [...mobileMenu.querySelectorAll('[data-mobile-panel]')] : []
  const closeMobilePanels = () => {
    mobilePanels.forEach((panel) => {
      panel.classList.add('translate-x-full')
      window.setTimeout(() => { panel.hidden = true }, 300)
    })
  }
  mobileMenu?.querySelectorAll('[data-mobile-panel-open]').forEach((button) => {
    button.addEventListener('click', () => {
      const panel = document.getElementById(button.dataset.mobilePanelOpen)
      if (!panel) return
      panel.hidden = false
      window.requestAnimationFrame(() => panel.classList.remove('translate-x-full'))
      panel.querySelector('button, a')?.focus()
    })
  })
  mobileMenu?.querySelectorAll('[data-mobile-panel-back]').forEach((button) => {
    button.addEventListener('click', () => {
      const panel = button.closest('[data-mobile-panel]')
      if (!panel) return
      panel.classList.add('translate-x-full')
      window.setTimeout(() => { panel.hidden = true }, 300)
    })
  })

  const searchToggles = [...document.querySelectorAll('[data-search-toggle]')]
  const searchInput = searchPanel?.querySelector('[data-search-input]')
  const searchResults = searchPanel?.querySelector('[data-search-results]')
  const searchHint = searchPanel?.querySelector('[data-search-hint]')
  const searchLoading = searchPanel?.querySelector('[data-search-loading]')
  const searchAllLink = searchPanel?.querySelector('[data-search-all-link]')
  const searchTabs = searchPanel ? [...searchPanel.querySelectorAll('[data-search-tab]')] : []
  const searchTabPanels = searchPanel ? [...searchPanel.querySelectorAll('[data-search-tab-panel]')] : []
  let activeSearchTab = 'recipes'
  let searchRequestController
  let searchDebounce
  let searchSequence = 0

  const setSearchTab = (type) => {
    activeSearchTab = type
    searchTabs.forEach((tab) => {
      const selected = tab.dataset.searchTab === type
      tab.setAttribute('aria-selected', String(selected))
      tab.tabIndex = selected ? 0 : -1
    })
    searchTabPanels.forEach((panel) => { panel.hidden = panel.dataset.searchTabPanel !== type })
  }

  const closeSearchPanel = ({ returnFocus = true } = {}) => {
    if (!searchPanel || searchPanel.hidden) return
    transitionPanel(searchPanel, false, setBodyLock)
    searchToggles.forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false')
      toggle.setAttribute('aria-label', 'Apri la ricerca')
      toggle.innerHTML = '<i data-lucide="search" class="size-5" aria-hidden="true"></i>'
    })
    createIcons({ icons: lucideIcons })
    if (returnFocus) searchToggles.find((toggle) => !toggle.hasAttribute('data-search-close'))?.focus()
  }

  const openSearchPanel = () => {
    if (!searchPanel) return
    closeMegaMenus()
    transitionPanel(searchPanel, true)
    searchToggles.forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'true')
      toggle.setAttribute('aria-label', 'Chiudi la ricerca')
      toggle.innerHTML = '<i data-lucide="x" class="size-5" aria-hidden="true"></i>'
    })
    createIcons({ icons: lucideIcons })
    setBodyLock()
    searchInput?.focus()
  }

  const buildSearchResultCard = (result) => {
    const isTag = result.presentation === 'tag'
    const isRecipeCategory = result.presentation === 'recipe_category'
    const link = document.createElement('a')
    link.href = result.url
    link.className = `group ${isRecipeCategory ? 'flex' : 'grid grid-cols-[5rem_minmax(0,1fr)_auto] sm:grid-cols-[5rem_minmax(0,1fr)_auto]'} items-center gap-4 rounded-2xl border border-black/10 bg-white p-3 transition hover:-translate-y-0.5 hover:border-brand-red/30 hover:shadow-lg`

    const imageWrap = document.createElement('div')
    imageWrap.className = isTag ? 'flex size-16 items-center justify-center rounded-full bg-brand-red text-white sm:size-20' : 'size-16 overflow-hidden rounded-full bg-cream sm:size-20'
    if (isTag) {
      const icon = document.createElement('i')
      icon.dataset.lucide = 'tag'
      icon.className = 'size-6'
      icon.setAttribute('aria-hidden', 'true')
      imageWrap.append(icon)
    } else {
      const image = document.createElement('img')
      image.src = result.image_url
      image.alt = result.title
      image.loading = 'lazy'
      image.className = 'h-full w-full object-cover transition duration-500 group-hover:scale-105'
      imageWrap.append(image)
    }

    const content = document.createElement('div')
    content.className = 'min-w-0 py-1'
    const label = document.createElement('p')
    label.className = 'text-[0.65rem] font-bold uppercase tracking-[0.16em] text-brand-blue'
    label.textContent = result.label
    const title = document.createElement('h3')
    title.className = 'mt-1 font-serif text-xl leading-tight group-hover:text-brand-red sm:text-2xl'
    const appendHighlightedText = (element, value) => {
      if (!result.matched_term) {
        element.textContent = value
        return
      }
      const escapedTerm = result.matched_term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const expression = new RegExp(`(${escapedTerm})`, 'ig')
      let offset = 0
      for (const match of value.matchAll(expression)) {
        element.append(document.createTextNode(value.slice(offset, match.index)))
        const before = value[match.index - 1]
        const after = value[match.index + match[0].length]
        const insideWord = (before && /[\p{L}\p{N}]/u.test(before)) || (after && /[\p{L}\p{N}]/u.test(after))
        if (insideWord) {
          element.append(document.createTextNode(match[0]))
        } else {
          const strong = document.createElement('strong')
          strong.className = 'font-bold text-ink'
          strong.textContent = match[0]
          element.append(strong)
        }
        offset = match.index + match[0].length
      }
      element.append(document.createTextNode(value.slice(offset)))
    }
    appendHighlightedText(title, result.title)
    content.append(label, title)
    const arrow = document.createElement('span')
    arrow.className = 'mr-2 text-xl text-brand-red transition group-hover:translate-x-1'
    arrow.setAttribute('aria-hidden', 'true')
    arrow.textContent = '→'
    if (!isRecipeCategory) link.append(imageWrap)
    link.append(content, arrow)
    return link
  }

  const renderSearchResults = (results) => {
    searchTabPanels.forEach((panel) => panel.replaceChildren())
    const visibleTypes = []
    searchTabs.forEach((tab) => {
      const type = tab.dataset.searchTab
      const entries = results[type] || []
      tab.hidden = entries.length === 0
      const panel = searchPanel?.querySelector(`[data-search-tab-panel="${type}"]`)
      panel?.append(...entries.map(buildSearchResultCard))
      if (entries.length > 0) visibleTypes.push(type)
    })
    createIcons({ icons: lucideIcons })
    if (!visibleTypes.includes(activeSearchTab)) activeSearchTab = visibleTypes[0]
    if (activeSearchTab) setSearchTab(activeSearchTab)
    return visibleTypes.length > 0
  }

  const resetSearchSuggestions = () => {
    searchSequence += 1
    searchRequestController?.abort()
    searchResults?.setAttribute('hidden', '')
    searchHint?.removeAttribute('hidden')
    if (searchHint) searchHint.textContent = 'Inizia a digitare almeno tre caratteri.'
    searchLoading?.classList.add('hidden')
    searchLoading?.classList.remove('flex')
  }

  searchToggles.forEach((button) => {
    button.addEventListener('click', () => {
      if (searchPanel?.hidden) openSearchPanel()
      else closeSearchPanel()
    })
  })

  searchTabs.forEach((tab) => tab.addEventListener('click', () => setSearchTab(tab.dataset.searchTab)))

  searchInput?.addEventListener('input', () => {
    const query = searchInput.value.trim()
    window.clearTimeout(searchDebounce)
    if (query.length < 3) {
      resetSearchSuggestions()
      return
    }

    searchHint?.removeAttribute('hidden')
    if (searchHint) searchHint.textContent = 'Attendo un attimo prima di cercare…'
    searchDebounce = window.setTimeout(async () => {
      const requestSequence = ++searchSequence
      searchRequestController?.abort()
      searchRequestController = new AbortController()
      searchHint?.setAttribute('hidden', '')
      searchLoading?.classList.remove('hidden')
      searchLoading?.classList.add('flex')
      try {
        const response = await fetch(`${searchInput.dataset.searchSuggestionsUrl}?q=${encodeURIComponent(query)}`, {
          headers: { Accept: 'application/json' },
          signal: searchRequestController.signal,
        })
        if (!response.ok) throw new Error('Search request failed')
        const payload = await response.json()
        if (searchInput.value.trim() !== query || requestSequence !== searchSequence) return
        const hasResults = renderSearchResults(payload.results)
        if (hasResults) searchResults?.removeAttribute('hidden')
        else searchResults?.setAttribute('hidden', '')
        if (!hasResults) {
          searchHint?.removeAttribute('hidden')
          if (searchHint) searchHint.textContent = 'Nessun risultato per questa ricerca.'
        }
        if (searchAllLink) searchAllLink.href = `${searchAllLink.dataset.searchAllBaseUrl}?q=${encodeURIComponent(query)}`
      } catch (error) {
        if (error.name === 'AbortError') return
        searchHint?.removeAttribute('hidden')
        if (searchHint) searchHint.textContent = 'La ricerca non è disponibile in questo momento.'
      } finally {
        if (requestSequence === searchSequence) {
          searchLoading?.classList.add('hidden')
          searchLoading?.classList.remove('flex')
        }
      }
    }, 350)
  })

  document.addEventListener('click', (event) => {
    if (header && !header.contains(event.target)) closeMegaMenus()
  })

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeMegaMenus()
      if (mobileMenu) mobileMenu.hidden = true
      closeSearchPanel({ returnFocus: false })
      closeFilterDrawer()
      setBodyLock()
      return
    }

    if (event.key !== 'Tab' || !filterDrawer || filterDrawer.hidden) return
    const focusable = [...filterDrawer.querySelectorAll('button, a, input, summary, [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.disabled && element.offsetParent !== null)
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  })

  const updateHeader = () => header?.toggleAttribute('data-scrolled', window.scrollY > 16)
  updateHeader()
  window.addEventListener('scroll', updateHeader, { passive: true })

  document.querySelectorAll('[data-expandable-text]').forEach((root) => {
    const content = root.querySelector('[data-expandable-content]')
    const toggle = root.querySelector('[data-expandable-toggle]')
    const label = root.querySelector('[data-expandable-label]')
    const fade = root.querySelector('[data-expandable-fade]')
    const icon = toggle?.querySelector('[data-lucide]')
    if (!content || !toggle || !label) return

    const collapsedHeight = 128
    if (content.scrollHeight <= collapsedHeight) return

    let expanded = false
    content.style.maxHeight = `${collapsedHeight}px`
    toggle.hidden = false
    if (fade) fade.hidden = false

    toggle.addEventListener('click', () => {
      expanded = !expanded
      content.style.maxHeight = expanded ? `${content.scrollHeight}px` : `${collapsedHeight}px`
      toggle.setAttribute('aria-expanded', String(expanded))
      label.textContent = expanded ? 'Leggi di meno' : 'Leggi di più'
      icon?.classList.toggle('rotate-180', expanded)
      if (fade) fade.hidden = expanded
    })

    window.addEventListener('resize', () => {
      if (expanded) content.style.maxHeight = `${content.scrollHeight}px`
    })
  })

  const recipeList = document.querySelector('[data-recipe-list]')
  const recipeViewToggles = [...document.querySelectorAll('[data-recipe-view-toggle]')]
  const recipeCardLinks = recipeList ? [...recipeList.querySelectorAll('[data-recipe-card-link]')] : []
  const recipeCardImages = recipeList ? [...recipeList.querySelectorAll('[data-recipe-card-image]')] : []
  const recipeCardArrows = recipeList ? [...recipeList.querySelectorAll('[data-recipe-card-arrow]')] : []
  const recipeCardTags = recipeList ? [...recipeList.querySelectorAll('[data-recipe-tags]')] : []

  const setRecipeView = (view) => {
    if (!recipeList || !['list', 'grid'].includes(view)) return
    const isGrid = view === 'grid'
    recipeList.dataset.view = view
    recipeList.classList.toggle('space-y-4', !isGrid)
    recipeList.classList.toggle('grid', isGrid)
    recipeList.classList.toggle('gap-5', isGrid)
  recipeList.classList.toggle('sm:grid-cols-2', isGrid)
  recipeList.classList.toggle('xl:grid-cols-4', isGrid)

    recipeCardLinks.forEach((link) => {
      link.classList.toggle('grid-cols-[6.5rem_minmax(0,1fr)_auto]', !isGrid)
      link.classList.toggle('sm:grid-cols-[10rem_minmax(0,1fr)_auto]', !isGrid)
      link.classList.toggle('grid-cols-1', isGrid)
      link.classList.toggle('sm:grid-cols-1', isGrid)
      link.classList.toggle('gap-5', !isGrid)
      link.classList.toggle('sm:gap-7', !isGrid)
      link.classList.toggle('gap-0', isGrid)
    })
    recipeCardImages.forEach((image) => {
      image.classList.toggle('aspect-square', !isGrid)
      image.classList.toggle('sm:aspect-[4/3]', !isGrid)
      image.classList.toggle('aspect-[4/3]', isGrid)
      image.classList.toggle('rounded-xl', !isGrid)
      image.classList.toggle('rounded-t-xl', isGrid)
    })
    recipeCardArrows.forEach((arrow) => arrow.classList.toggle('hidden', isGrid))
    recipeCardTags.forEach((tags) => {
      tags.classList.toggle('hidden', !isGrid)
      tags.classList.toggle('flex', isGrid)
    })
    recipeViewToggles.forEach((toggle) => {
      const selected = toggle.dataset.recipeViewToggle === view
      toggle.setAttribute('aria-pressed', String(selected))
      toggle.classList.toggle('bg-brand-red', selected)
      toggle.classList.toggle('text-white', selected)
      toggle.classList.toggle('text-ink/55', !selected)
    })
    window.localStorage.setItem('mbene-recipe-view', view)
  }

  if (recipeList && recipeViewToggles.length > 0) {
    const savedRecipeView = window.localStorage.getItem('mbene-recipe-view')
    if (savedRecipeView) setRecipeView(savedRecipeView)
    recipeViewToggles.forEach((toggle) => toggle.addEventListener('click', () => setRecipeView(toggle.dataset.recipeViewToggle)))
  }

  const recipeHistoryNode = document.querySelector('[data-recipe-history]')
  if (recipeHistoryNode) {
    try {
      const currentRecipe = JSON.parse(recipeHistoryNode.dataset.recipeHistoryPayload)
      const historyKey = 'mbene-recipe-history'
      const savedHistory = JSON.parse(window.localStorage.getItem(historyKey) || '[]')
      const uniqueHistory = Array.isArray(savedHistory)
        ? savedHistory.filter((recipe, index, entries) => recipe?.id && recipe.id !== currentRecipe.id && entries.findIndex((entry) => entry?.id === recipe.id) === index)
        : []
      const recentRecipes = uniqueHistory.slice(0, 12)
      const nextHistory = [currentRecipe, ...recentRecipes].slice(0, 13)
      window.localStorage.setItem(historyKey, JSON.stringify(nextHistory))

      if (recentRecipes.length >= 5) {
        const recentSection = document.querySelector('[data-recent-recipes-section]')
        const recentList = document.querySelector('[data-recent-recipes-list]')
        if (recentSection && recentList) {
          const buildRecentLink = (recipe) => {
            const slide = document.createElement('div')
            slide.className = 'min-w-0 flex-[0_0_86%] pl-4 sm:flex-[0_0_48%] lg:flex-[0_0_20%]'
            slide.setAttribute('role', 'group')
            slide.setAttribute('aria-roledescription', 'slide')
            const link = document.createElement('a')
            link.href = recipe.url
            link.className = 'group flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-black/10 bg-white transition hover:border-brand-red/30 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2'
            const image = document.createElement('img')
            image.src = recipe.image_url
            image.alt = ''
            image.loading = 'lazy'
            image.decoding = 'async'
            image.className = 'aspect-[4/3] w-full bg-cream object-cover'
            const content = document.createElement('span')
            content.className = 'flex min-w-0 flex-1 flex-col p-4'
            const category = document.createElement('span')
            category.className = 'block truncate text-[0.6rem] font-bold uppercase tracking-[0.14em] text-brand-blue'
            category.textContent = recipe.category
            const title = document.createElement('span')
            title.className = 'mt-2 block line-clamp-3 font-serif text-xl leading-tight group-hover:text-brand-red'
            title.textContent = recipe.title
            content.append(category, title)
            link.append(image, content)
            slide.append(link)
            return slide
          }

          recentList.replaceChildren(...recentRecipes.map(buildRecentLink))
          recentSection.hidden = false
          initializeCarousel(recentSection.querySelector('[data-embla]'))
        }
      }
    } catch (_error) {
      // Recent recipes are an optional local enhancement; rendering must remain unaffected.
    }
  }

  document.querySelectorAll('[data-content-tabs]').forEach((root) => {
    const buttons = [...root.querySelectorAll('[data-content-tab-button]')]
    const panels = [...root.querySelectorAll('[data-content-tab-panel]')]
    const selectTab = (tab) => {
      buttons.forEach((button) => {
        const selected = button.dataset.contentTabButton === tab
        button.setAttribute('aria-selected', String(selected))
        button.tabIndex = selected ? 0 : -1
        button.classList.toggle('border-brand-red', selected)
        button.classList.toggle('text-brand-red', selected)
        button.classList.toggle('border-transparent', !selected)
        button.classList.toggle('text-ink/45', !selected)
      })
      panels.forEach((panel) => { panel.hidden = panel.dataset.contentTabPanel !== tab })
    }

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => selectTab(button.dataset.contentTabButton))
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
        event.preventDefault()
        const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length
        buttons[nextIndex].focus()
        selectTab(buttons[nextIndex].dataset.contentTabButton)
      })
    })
  })
})
